import { useQuery } from 'react-query';

import { PROJECTED_CREDIT_LIMIT_MESSAGE } from '../../../../backend/src/utils/billing-utils';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { stagedPagesFromFiles } from '@/new/lib/projected-credit-limit';

interface ProjectedCreditLimitResponse {
  wouldExceed: boolean;
  message: string | null;
}

export function useProjectedCreditLimitBlock(
  matterId: string | undefined,
  files: Array<{ numberOfPages?: number | null }> | undefined,
): {
  wouldExceed: boolean;
  message: string | null;
} {
  const { fetchConfigGET } = useGetFetchConfig();
  const stagedPages = stagedPagesFromFiles(files);
  const { data } = useQuery<ProjectedCreditLimitResponse>(
    [QueryEntity.ProjectedCreditLimit, { matterId, stagedPages }],
    async () => {
      const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/projected-credit-limit`, fetchConfigGET);
      if (!response.ok) {
        throw new Error('Failed to check projected credit usage');
      }
      return (await response.json()) as ProjectedCreditLimitResponse;
    },
    {
      enabled: matterId !== undefined && (files?.length ?? 0) > 0,
      refetchOnWindowFocus: true,
      refetchInterval: 30_000,
    },
  );

  const wouldExceed = data?.wouldExceed ?? false;

  return {
    wouldExceed,
    // The backend explains which limit the batch would exceed; fall back if it sends none.
    message: wouldExceed ? (data?.message ?? PROJECTED_CREDIT_LIMIT_MESSAGE) : null,
  };
}
